import { useWebsite } from '../context/WebsiteContext';
import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

const FacebookIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
  </svg>
);

const Footer = () => {
  const { config: website } = useWebsite();
  return (
    <footer className="bg-[#1B3A5C] text-white" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src={website.logo}
              alt="Kre8ing Your IdeaS"
              className="h-14 w-auto mb-6 brightness-0 invert opacity-90"
            />
            <p className="text-blue-200/60 text-sm leading-relaxed mb-6">
              Family-owned embroidery company cultivating creativity through personalized apparel and
              accessories since 1996.
            </p>
            <div className="flex space-x-3">
              <a
                href="https://web.facebook.com/kre8ingyourideas"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 border border-white/15 rounded-xl text-white/60 hover:border-[#F5A623] hover:text-[#F5A623] hover:bg-[#F5A623]/10 transition-all"
                data-testid="social-facebook"
              >
                <FacebookIcon />
              </a>
              <a
                href="https://www.instagram.com/kre8ing_your_ideas/"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 border border-white/15 rounded-xl text-white/60 hover:border-[#F5A623] hover:text-[#F5A623] hover:bg-[#F5A623]/10 transition-all"
                data-testid="social-instagram"
              >
                <InstagramIcon />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-base mb-6 text-white">Quick Links</h4>
            <ul className="space-y-3">
              {[
                { name: 'Shop', path: '/shop' },
                { name: 'Back To School', path: '/back-to-school' },
                { name: 'Services', path: '/services' },
                { name: 'About Us', path: '/about' },
                { name: 'FAQ', path: '/faq' },
                { name: 'Contact', path: '/contact' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-blue-200/50 hover:text-[#F5A623] transition-colors text-sm"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-bold text-base mb-6 text-white">Our Services</h4>
            <ul className="space-y-3">
              {[
                'Custom Embroidery',
                'DTG Printing',
                'School Uniforms',
                'Greek Apparel',
                'Business Branding',
                'Personalized Gifts',
              ].map((service) => (
                <li key={service}>
                  <Link
                    to="/services"
                    className="text-blue-200/50 hover:text-[#F5A623] transition-colors text-sm"
                  >
                    {service}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-bold text-base mb-6 text-white">Contact Info</h4>
            <ul className="space-y-4">
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-[#F5A623] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-blue-200/60 text-sm">
                  129 Slade Ave<br />Pikesville, MD 21208
                </span>
              </li>
              <li>
                <a
                  href="tel:443-940-6660"
                  className="flex items-center space-x-3 text-blue-200/60 hover:text-[#F5A623] transition-colors"
                >
                  <Phone className="w-5 h-5 text-[#F5A623]" strokeWidth={1.5} />
                  <span className="text-sm">443-940-6660</span>
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@shopkyis.com"
                  className="flex items-center space-x-3 text-blue-200/60 hover:text-[#F5A623] transition-colors"
                >
                  <Mail className="w-5 h-5 text-[#F5A623]" strokeWidth={1.5} />
                  <span className="text-sm">info@shopkyis.com</span>
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <Clock className="w-5 h-5 text-[#F5A623] flex-shrink-0 mt-0.5" strokeWidth={1.5} />
                <span className="text-blue-200/60 text-sm">
                  Mon - Fri: 10am - 6pm<br />Sat - Sun: Closed
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Partner logos */}
      <div className="border-t border-white/10 py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-center space-x-8">
          <img
            src="https://shopkyis.com/wp-content/uploads/2024/10/GBCC-Logo-_One-Color-Teal.png"
            alt="GBCC"
            className="h-10 w-auto opacity-40 brightness-0 invert"
          />
          <img
            src="https://shopkyis.com/wp-content/uploads/2024/10/Light_10K_SB_ALUMNI.png"
            alt="10K SB Alumni"
            className="h-10 w-auto opacity-40 brightness-0 invert"
          />
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10 bg-[#142D4A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-3 md:space-y-0">
            <p className="text-blue-200/40 text-sm">
              &copy; {new Date().getFullYear()} Kre8ing Your IdeaS. All rights reserved.
            </p>
            <div className="flex space-x-6 items-center">
              <a
                href="https://shopkyis.com/wp-content/uploads/2025/02/KYIS-privacy-policy.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-200/40 hover:text-[#F5A623] text-sm transition-colors"
              >
                Privacy Policy
              </a>
              <a
                href="https://shopkyis.com/wp-content/uploads/2025/02/KYIS-returns.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-200/40 hover:text-[#F5A623] text-sm transition-colors"
              >
                Terms &amp; Conditions
              </a>
              <Link
                to="/admin/login"
                className="text-blue-200/25 hover:text-blue-200/50 text-xs transition-colors"
                data-testid="admin-login-link"
              >
                Admin
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
